import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ExtractionAuditSection from "./ui/ExtractionAuditSection";
import { isAuditFlagged } from "../lib/extractionAudit";
import { api, fileUrl, getAuthHeaders, type SearchResult } from "../api";
import {
  SIGNIFICANCE_EMOJI,
  SIGNIFICANCE_LABELS,
  parseExtractionConfidence,
  type Annotation,
  type CaseRecord,
  type CaseVolume,
  type ExtractionConfidence,
  type RecordAttachment,
  type RecordVersion,
  type Significance,
} from "../types";
import VolumeSummary from "./VolumeSummary";
import { useSettings } from "../lib/useSettings";
import { describeRecord, type RecordCategory } from "../lib/recordMeta";
import { CategoryBar, CategoryBadge } from "./RecordCategoryUI";
import { effectiveCategory, categoryMeta, type CategoryId } from "../lib/recordCategory";
import {
  defaultViewMode,
  loadViewMode,
  saveViewMode,
  type ViewMode,
} from "../lib/viewModeStore";
import { useModalEscape } from "../lib/useModalEscape";
import { useIsMobile } from "../lib/useBreakpoint";
import {
  formatDate,
  formatDateTime,
  formatMonthYear,
  formatTime as formatTimeUtc,
} from "../lib/formatDate";
import { Badge } from "./ui/Badge";
import { Button } from "./ui/Button";
import Tooltip from "./ui/Tooltip";
import ModalBackdrop from "./ui/ModalBackdrop";
import { AttachmentCard, attachmentEmoji, attachmentKind } from "./AttachmentCard";
import ConfidenceDot from "./ConfidenceDot";
import AuthImage from "./AuthImage";
import ZoomableLightbox from "./ZoomableLightbox";

// ---------------------------------------------------------------------------
// Timeline — three view modes, one shared expanded layout.
//
// Compact    — scannable table rows, inline expand on click
// Comfortable — card with 2–3 line preview, same expand shape
// Reading    — full body text, paginated 10-per-page
//
// All three render the same <RecordExpanded> layout when a row is
// opened, so features like annotations, tags, versioning, attachment
// cards, and the diff view are defined once.
//
// Non-email records (video/audio/screenshot/file) drop the From/To
// columns in Compact mode and widen the Subject/Title column — email
// remains the only category where "who" is worth a dedicated column.
// ---------------------------------------------------------------------------

type TypeFilter =
  | "all"
  | "email"
  | "video"
  | "audio"
  | "screenshot"
  | "file"
  | "other";
type SignificanceFilter = "all" | "high" | "medium" | "low";

const PAGE_SIZE_READING = 10;

function parseTagsString(s: string | null | undefined): string[] {
  if (!s) return [];
  return s
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean);
}

// Every date-display helper in the Records view delegates to the
// shared lib/formatDate utility so the whole module renders dates
// in a single UTC-anchored style. Year is always included so
// "Dec 18, 2025" never flips to a bare "Dec 18" based on the
// reader's current calendar year.
function monthYearLabel(iso: string): string {
  return formatMonthYear(iso) || "Unknown date";
}

function formatShortDate(iso: string): string {
  return formatDate(iso, "short") || iso;
}

function formatTime(iso: string): string {
  return formatTimeUtc(iso);
}

function truncate(s: string | null | undefined, n: number): string {
  if (!s) return "";
  const clean = s.replace(/\s+/g, " ").trim();
  return clean.length <= n ? clean : clean.slice(0, n - 1) + "…";
}

function bodyPreview(r: CaseRecord, maxChars: number): string {
  const body = r.body_text ?? r.ocr_text ?? "";
  if (!body) return "";
  // Skip bracketed framing markers ([EMAIL BODY], [ATTACHMENT: …])
  const lines = body.split(/\r?\n/);
  const meaningful: string[] = [];
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;
    if (/^\[[^\]]{1,80}\]$/.test(line)) continue;
    meaningful.push(line);
    if (meaningful.join(" ").length >= maxChars) break;
  }
  return truncate(meaningful.join(" "), maxChars);
}

function sigTone(
  level: Significance,
): "high" | "medium" | "low" {
  return level;
}

// Small chip rendered next to a record's Date in the expanded view.
// Communicates how trustworthy the date is without drawing too much
// attention: "header" / "forwarded_header" are authoritative (green),
// "body" is extracted from prose and could be wrong (amber), "upload"
// means we defaulted to the filing time (amber), "manual" means the
// user typed it (blue). Null or unknown sources render nothing.
function DateSourceLabel({
  source,
}: {
  source: CaseRecord["date_source"];
}) {
  if (!source) return null;
  const label =
    source === "header"
      ? "email header"
      : source === "forwarded_header"
        ? "forwarded header"
        : source === "body"
          ? "body text"
          : source === "upload"
            ? "upload time"
            : source === "manual"
              ? "entered manually"
              : null;
  if (!label) return null;
  const tone: "strong" | "soft" | "weak" =
    source === "header" || source === "forwarded_header"
      ? "strong"
      : source === "manual"
        ? "soft"
        : "weak";
  const tooltip =
    source === "header"
      ? "From the email's MIME Date header — authoritative."
      : source === "forwarded_header"
        ? "From the original email's header inside a forwarded body."
        : source === "body"
          ? "Extracted from body text. Verify via Repair dates if suspect."
          : source === "upload"
            ? "No date found in content — using the upload time."
            : "Entered manually by the case owner.";
  return (
    <span
      className={`date-source-label date-source-${tone}`}
      title={tooltip}
    >
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Records are never grouped. The date + subject "duplicate cluster" grouping
// (and its collapse/expand line) was removed: same-day records with similar
// subjects are reply chains and forwards, not duplicates. The only duplicate
// tooling is the manual "Find duplicates" 90% Jaccard content scan (see the
// deduplicate endpoint). DuplicateCluster stays only as an inert type for the
// view-context plumbing, which now always carries empty maps.
interface DuplicateCluster {
  leaderId: string;
  followerIds: Set<string>;
  date: string;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface TimelineProps {
  caseId: string;
  records: CaseRecord[];
  onDeleted: (ids: string[]) => void;
  onRecordCreated?: (record: CaseRecord) => void;
  onRecordUpdated: (record: CaseRecord) => void;
  onRefresh: () => void;
  // Volume data. When the case has >1 volume the Timeline splits
  // records into per-volume sections with collapsible headers;
  // single-volume cases (the common case) render exactly as before.
  volumes?: CaseVolume[];
  // One-shot "show this volume's summary" trigger from the sidebar
  // kebab menu's "View summary" action. When set, Timeline force-
  // expands the named volume's section and scrolls the summary block
  // into view, then calls onShowSummaryHandled to clear it.
  showSummaryVolumeId?: string | null;
  onShowSummaryHandled?: () => void;
  // Triggered by the Timeline's per-volume summary expander's "Ask AI
  // about this volume" button. CaseView swaps to the Interrogate tab
  // and sets the scope to that volume.
  onAskAiAboutVolume?: (volumeId: string) => void;
  // One-shot "scroll this record into view + expand it" trigger from
  // AI citation clicks and the Repair dates modal. When set, Timeline
  // finds the matching DOM row, expands it, and scrolls it into the
  // viewport, then calls onScrollToRecordHandled to clear the state.
  //
  // Shape is `{id, n}` rather than a bare string so every click
  // produces a fresh object reference — React bails on identical
  // state values, and the same-id case (click the same record
  // twice) would otherwise silently no-op.
  // attachmentId deep-links a specific attachment (e.g. a summary row whose
  // fact came from a PDF/image attachment) — the matching AttachmentCard
  // auto-opens its inline viewer, reusing the search deep-link path.
  scrollToRecordId?: { id: string; n: number; attachmentId?: string | null } | null;
  onScrollToRecordHandled?: () => void;
  // When true, the list filters to records whose extraction
  // confidence for the date is "fallback" (no date found in content
  // — using filing date). Driven from the data-quality stat in the
  // Overview / case header so the user can fix bad dates in bulk.
  onlyFallbackDates?: boolean;
  onFallbackFilterHandled?: () => void;
  // "Show routine" state lives in CaseView so the Records tab label
  // can report the visible count in lockstep with the toggle.
  // `hiddenRoutineCount` is the number of low-significance records
  // hidden across the whole dataset (not the view-filtered subset)
  // so the toggle label stays stable as other filters change.
  showRoutine: boolean;
  onShowRoutineChange: (v: boolean) => void;
  hiddenRoutineCount: number;
}

// Render a search snippet whose matched span is delimited by U+0002/U+0003
// control chars (from FTS5 snippet()). Builds <mark> elements from text
// nodes only — never innerHTML — so untrusted record/OCR text can't inject
// markup.
function renderSnippet(snip: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  const re = /\u0002([^\u0003]*)\u0003/g;
  let last = 0;
  let key = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(snip)) !== null) {
    if (m.index > last) parts.push(snip.slice(last, m.index));
    parts.push(<mark key={key++}>{m[1]}</mark>);
    last = re.lastIndex;
  }
  if (last < snip.length) parts.push(snip.slice(last));
  return parts;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function Timeline({
  caseId,
  records,
  onDeleted,
  onRecordUpdated,
  onRefresh,
  volumes = [],
  showSummaryVolumeId = null,
  onShowSummaryHandled,
  onAskAiAboutVolume,
  scrollToRecordId = null,
  onScrollToRecordHandled,
  onlyFallbackDates = false,
  onFallbackFilterHandled,
  showRoutine,
  onShowRoutineChange,
  hiddenRoutineCount,
}: TimelineProps) {
  const settings = useSettings();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [selectionMode, setSelectionMode] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkError, setBulkError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");
  const [sigFilter, setSigFilter] = useState<SignificanceFilter>("all");
  const [categoryFilter, setCategoryFilter] = useState<CategoryId | "all">("all");
  const [keyOnly, setKeyOnly] = useState(false);
  const [tagFilter, setTagFilter] = useState<Set<string>>(new Set());
  const [dedupBusy, setDedupBusy] = useState(false);
  const [dedupMessage, setDedupMessage] = useState<string | null>(null);
  const [lightbox, setLightbox] = useState<{
    url: string;
    filename: string;
  } | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [expandedClusters, setExpandedClusters] = useState<Set<string>>(
    new Set(),
  );
  const [flashingId, setFlashingId] = useState<string | null>(null);
  // Server full-text search (subject/body/ocr/notes + attachment text),
  // ranked, with snippets. Runs for queries ≥2 chars; the in-place
  // client-side filter below handles 1-char/typing. focusAttachmentId
  // deep-links a PDF hit into its inline viewer.
  const [searchResults, setSearchResults] = useState<SearchResult[] | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [focusAttachmentId, setFocusAttachmentId] = useState<string | null>(null);
  const [readingPage, setReadingPage] = useState(0);
  const [storedViewMode, setViewModeState] = useState<ViewMode>(() => {
    return loadViewMode(caseId) ?? defaultViewMode(records.length);
  });
  const isMobile = useIsMobile();
  // On mobile the compact table is too narrow to be useful — force
  // comfortable cards regardless of the user's stored preference.
  const viewMode: ViewMode = isMobile ? "comfortable" : storedViewMode;
  function setViewMode(mode: ViewMode) {
    setViewModeState(mode);
    saveViewMode(caseId, mode);
    setReadingPage(0);
  }

  function toggleTagFilter(tag: string) {
    setTagFilter((prev) => {
      const next = new Set(prev);
      if (next.has(tag)) next.delete(tag);
      else next.add(tag);
      return next;
    });
  }

  function toggleExpanded(id: string) {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function toggleCluster(leaderId: string) {
    setExpandedClusters((prev) => {
      const next = new Set(prev);
      if (next.has(leaderId)) next.delete(leaderId);
      else next.add(leaderId);
      return next;
    });
  }

  const duplicateCount = useMemo(
    () => records.filter((r) => r.is_duplicate).length,
    [records],
  );

  const allTagsInCase = useMemo(() => {
    const set = new Set<string>();
    for (const r of records)
      for (const t of parseTagsString(r.tags)) set.add(t);
    return Array.from(set).sort();
  }, [records]);

  const describedByRecordId = useMemo(() => {
    const m = new Map<string, ReturnType<typeof describeRecord>>();
    for (const r of records) m.set(r.id, describeRecord(r));
    return m;
  }, [records]);

  const recordsById = useMemo(() => {
    const m = new Map<string, CaseRecord>();
    for (const r of records) m.set(r.id, r);
    return m;
  }, [records]);


  // Canonical ordering across all three views: record_date, in the
  // direction the user picked in Settings. Default is newest-first.
  // Every view (Compact / Comfortable / Reading) renders from this
  // single list so they always agree on membership and order.
  const sortedRecords = useMemo(() => {
    const direction = settings.records.sortOrder === "newest" ? -1 : 1;
    return [...records].sort((a, b) => {
      if (a.record_date === b.record_date) return 0;
      return a.record_date < b.record_date ? -direction : direction;
    });
  }, [records, settings.records.sortOrder]);

  // Records filtered by search/type/significance/tag/fallback but BEFORE
  // the routine toggle. This is the "visible to AI / exports" list.
  const preRoutineFiltered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return sortedRecords.filter((r) => {
      const meta = describedByRecordId.get(r.id);
      const cat = meta?.category ?? "other";
      if (onlyFallbackDates) {
        const conf = parseExtractionConfidence(r.extraction_confidence);
        // Legacy records (pre-0035) have no confidence blob and are
        // excluded from the "needs review" filter — we can't tell
        // whether their date is reliable.
        if (!conf || conf.date !== "fallback") return false;
      }
      if (keyOnly && r.significance !== "high") return false;
      if (sigFilter !== "all" && r.significance !== sigFilter) return false;
      if (categoryFilter !== "all" && effectiveCategory(r) !== categoryFilter)
        return false;
      if (typeFilter !== "all") {
        // Normalise category groupings for the filter.
        if (typeFilter === "other") {
          if (
            cat === "email" ||
            cat === "video" ||
            cat === "audio" ||
            cat === "screenshot" ||
            cat === "file"
          ) {
            return false;
          }
        } else if (cat !== typeFilter) {
          return false;
        }
      }
      if (tagFilter.size > 0) {
        const tags = parseTagsString(r.tags);
        const ok = Array.from(tagFilter).every((t) => tags.includes(t));
        if (!ok) return false;
      }
      if (!q) return true;
      const hay = [
        r.subject,
        r.body_text,
        r.from_addr,
        r.to_addr,
        r.notes,
        r.ocr_text,
        r.file_name,
        r.tags,
        meta?.title,
      ]
        .filter((v): v is string => !!v)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [
    sortedRecords,
    describedByRecordId,
    search,
    typeFilter,
    sigFilter,
    categoryFilter,
    keyOnly,
    tagFilter,
    onlyFallbackDates,
  ]);

  // Routine filter is view-state only: records are hidden from the
  // list when `showRoutine` is off, but they still exist in the
  // dataset, still feed AI context, and still export in dossiers.
  const filteredRecords = useMemo(() => {
    if (showRoutine) return preRoutineFiltered;
    return preRoutineFiltered.filter((r) => r.significance !== "low");
  }, [preRoutineFiltered, showRoutine]);

  // Keep selection bounded to visible records.
  useEffect(() => {
    const visible = new Set(filteredRecords.map((r) => r.id));
    setSelected((prev) => {
      let changed = false;
      const next = new Set<string>();
      for (const id of prev) {
        if (visible.has(id)) next.add(id);
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [filteredRecords]);

  // Paginate in reading mode.
  useEffect(() => {
    setReadingPage(0);
  }, [search, typeFilter, sigFilter, keyOnly, tagFilter, viewMode]);

  const filteredCount = filteredRecords.length;
  const totalCount = records.length;
  const anyFilterActive =
    search.trim() !== "" ||
    typeFilter !== "all" ||
    sigFilter !== "all" ||
    keyOnly ||
    tagFilter.size > 0 ||
    onlyFallbackDates;

  // Pinned records float to the top; everything else retains the
  // canonical sort order. Both lists are flat — no threading, no
  // nesting. The three views share this exact shape so they can't
  // drift on membership or order.
  const orderedRecords = useMemo(() => {
    const pinned: CaseRecord[] = [];
    const unpinned: CaseRecord[] = [];
    for (const r of filteredRecords) {
      if (r.pinned) pinned.push(r);
      else unpinned.push(r);
    }
    return { pinned, unpinned };
  }, [filteredRecords]);

  // Duplicate clusters are computed on the unpinned list so pinned
  // records always render individually — pinning is a deliberate
  // user action and shouldn't be hidden behind a collapsed cluster.
  // Duplicate UI removed: true content duplicates (Jaccard >= 90%) are now
  // auto-removed at intake and by "Find duplicates", so there are no
  // per-record "Possible duplicate" banners or clusters to render.
  // Duplicate grouping removed — these stay empty so the view-context plumbing
  // (and its consumers) keep typechecking without rendering any cluster line.
  const duplicateClusters = useMemo(
    (): {
      clusters: Map<string, DuplicateCluster>;
      followerIds: Set<string>;
    } => ({ clusters: new Map(), followerIds: new Set() }),
    [],
  );

  // Bulk actions
  async function handleBulkDelete() {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    const msg =
      ids.length === 1
        ? "Delete 1 record? This cannot be undone."
        : `Delete ${ids.length} records? This cannot be undone.`;
    if (!window.confirm(msg)) return;
    setBulkDeleting(true);
    setBulkError(null);
    try {
      const results = await Promise.allSettled(
        ids.map((id) => api.deleteRecord(id)),
      );
      const done = ids.filter((_, i) => results[i]!.status === "fulfilled");
      const failed = results.filter((r) => r.status === "rejected").length;
      if (done.length > 0) onDeleted(done);
      setSelected((prev) => {
        const next = new Set(prev);
        for (const id of done) next.delete(id);
        return next;
      });
      if (failed > 0) {
        setBulkError(
          `${failed} of ${ids.length} could not be deleted. Try again.`,
        );
      } else {
        setSelectionMode(false);
      }
    } finally {
      setBulkDeleting(false);
    }
  }

  async function handleSingleDelete(id: string) {
    if (!window.confirm("Delete this record? This cannot be undone.")) return;
    try {
      await api.deleteRecord(id);
      onDeleted([id]);
      setSelected((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    } catch (err) {
      window.alert(`Delete failed: ${(err as Error).message}`);
    }
  }

  async function handleDismissDuplicate(id: string) {
    try {
      const { record } = await api.updateRecord(id, { is_duplicate: false });
      onRecordUpdated(record);
    } catch (err) {
      window.alert(`Dismiss failed: ${(err as Error).message}`);
    }
  }

  async function handleFindDuplicates() {
    setDedupBusy(true);
    setDedupMessage("Scanning for duplicates…");
    try {
      const result = await api.deduplicate(caseId);
      if (result.removed === 0) {
        setDedupMessage(
          `No duplicates found across ${result.scanned} record${
            result.scanned === 1 ? "" : "s"
          }.`,
        );
      } else {
        setDedupMessage(
          `Removed ${result.removed} duplicate record${
            result.removed === 1 ? "" : "s"
          }.`,
        );
      }
      onRefresh();
    } catch (err) {
      console.error("[dedup] failed:", err);
      setDedupMessage(`Scan failed: ${(err as Error).message}`);
    } finally {
      setDedupBusy(false);
    }
  }

  // Auto-dismiss the dedup toast after 5s so it doesn't linger forever.
  // Busy-state messages stay up while scanning.
  useEffect(() => {
    if (!dedupMessage) return;
    if (dedupBusy) return;
    const t = window.setTimeout(() => setDedupMessage(null), 5000);
    return () => window.clearTimeout(t);
  }, [dedupMessage, dedupBusy]);

  // AI-citation navigation: when a parent sets scrollToRecordId, find
  // the row/card in the DOM, expand it, scroll it into view, and flash
  // a 2s amber highlight so the user's eye lands on it. Rows + cards
  // both carry id={`record-${id}`}. Call onScrollToRecordHandled
  // afterwards so the parent can clear its state — the trigger is
  // one-shot, not sticky.
  useEffect(() => {
    if (!scrollToRecordId) return;
    const targetId = scrollToRecordId.id;

    // Expand the row first so the content is visible when we scroll.
    setExpandedIds((prev) => {
      if (prev.has(targetId)) return prev;
      const next = new Set(prev);
      next.add(targetId);
      return next;
    });
    // Deep-link an attachment if requested — the matching AttachmentCard
    // auto-opens its inline viewer (PDF iframe / image lightbox).
    setFocusAttachmentId(scrollToRecordId.attachmentId ?? null);

    // Tab switches, volume expansion, and view modes can delay when
    // the target node lands in the DOM. Retry across ~30 frames
    // (~500ms at 60fps) so the scroll still fires after the
    // Volume-Aware view expands a closed volume, then falls back to
    // one more attempt 500ms later for extra-slow renders (mostly a
    // safety net for low-end devices).
    let frames = 0;
    let raf = 0;
    let timeoutId = 0;
    const attempt = (): boolean => {
      const el = document.getElementById(`record-${targetId}`);
      if (!el) return false;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      setFlashingId(targetId);
      onScrollToRecordHandled?.();
      return true;
    };
    const tryScroll = () => {
      if (attempt()) return;
      if (frames++ < 30) {
        raf = requestAnimationFrame(tryScroll);
      } else {
        // One final retry after 500ms for slow-rendering cases
        // (e.g. very large record bodies). If this still misses,
        // clear parent state so we don't loop forever.
        timeoutId = window.setTimeout(() => {
          if (!attempt()) onScrollToRecordHandled?.();
        }, 500);
      }
    };
    raf = requestAnimationFrame(tryScroll);
    return () => {
      cancelAnimationFrame(raf);
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [scrollToRecordId, onScrollToRecordHandled]);

  // Clear the flashing id after the CSS animation has run. 2s is a
  // hair longer than the animation itself so React doesn't rip the
  // class off mid-flash.
  useEffect(() => {
    if (!flashingId) return;
    const t = window.setTimeout(() => setFlashingId(null), 2100);
    return () => window.clearTimeout(t);
  }, [flashingId]);

  // Debounced server search. Below 2 chars we clear results and let the
  // instant client-side filter handle the (sub)string. A stale-guard
  // (`active`) keeps an earlier slow response from overwriting a newer one.
  useEffect(() => {
    const q = search.trim();
    if (q.length < 2) {
      setSearchResults(null);
      setSearchLoading(false);
      return;
    }
    let active = true;
    setSearchLoading(true);
    const t = window.setTimeout(async () => {
      try {
        const { results } = await api.searchRecords(caseId, q);
        if (active) setSearchResults(results);
      } catch {
        if (active) setSearchResults([]);
      } finally {
        if (active) setSearchLoading(false);
      }
    }, 200);
    return () => {
      active = false;
      window.clearTimeout(t);
    };
  }, [search, caseId]);

  // Click-through from a search result: expand the record, scroll it into
  // view + flash it, and (for an attachment hit) signal its card to open
  // the inline viewer. Mirrors the AI-citation scroll behavior.
  const focusRecord = useCallback(
    (recordId: string, attachmentId?: string | null) => {
      setExpandedIds((prev) => {
        if (prev.has(recordId)) return prev;
        const next = new Set(prev);
        next.add(recordId);
        return next;
      });
      setFocusAttachmentId(attachmentId ?? null);
      let frames = 0;
      const attempt = (): boolean => {
        const el = document.getElementById(`record-${recordId}`);
        if (!el) return false;
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        setFlashingId(recordId);
        return true;
      };
      const tick = () => {
        if (attempt()) return;
        if (frames++ < 30) requestAnimationFrame(tick);
        else window.setTimeout(attempt, 500);
      };
      requestAnimationFrame(tick);
    },
    [],
  );

  // Clear the deep-link signal shortly after so it doesn't linger; each
  // AttachmentCard only auto-opens once per signal anyway.
  useEffect(() => {
    if (!focusAttachmentId) return;
    const t = window.setTimeout(() => setFocusAttachmentId(null), 1500);
    return () => window.clearTimeout(t);
  }, [focusAttachmentId]);

  function selectAllDuplicates() {
    const dupeIds = records.filter((r) => r.is_duplicate).map((r) => r.id);
    if (dupeIds.length === 0) return;
    setSelectionMode(true);
    setSelected(new Set(dupeIds));
  }

  // ---- Shared props bag for view renderers ----
  const viewCtx: ViewContext = {
    caseId,
    selectionMode,
    selected,
    expandedIds,
    expandedClusters,
    toggleExpanded,
    toggleCluster,
    duplicateClusters: duplicateClusters.clusters,
    duplicateFollowerIds: duplicateClusters.followerIds,
    onToggleSelect: (id) =>
      setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      }),
    onToggleSelectMany: (ids, on) =>
      setSelected((prev) => {
        const next = new Set(prev);
        for (const id of ids) {
          if (on) next.add(id);
          else next.delete(id);
        }
        return next;
      }),
    onSingleDelete: handleSingleDelete,
    onDismissDuplicate: handleDismissDuplicate,
    onRecordUpdated,
    onOpenLightbox: (url, filename) => setLightbox({ url, filename }),
    describedByRecordId,
    flashingId,
    focusAttachmentId,
  };

  // ---- Render ----
  const showToolbarFindDuplicates = records.length > 0;
  const showSelectAllDupes = false; // duplicate selection UI removed

  return (
    <div className="records-wrap timeline">
      <TimelineToolbar
        search={search}
        onSearch={setSearch}
        typeFilter={typeFilter}
        onTypeFilter={setTypeFilter}
        sigFilter={sigFilter}
        onSigFilter={setSigFilter}
        keyOnly={keyOnly}
        onToggleKeyOnly={() => setKeyOnly((v) => !v)}
        showRoutine={showRoutine}
        hiddenRoutineCount={hiddenRoutineCount}
        onToggleShowRoutine={() => onShowRoutineChange(!showRoutine)}
        allTags={allTagsInCase}
        tagFilter={tagFilter}
        onToggleTag={toggleTagFilter}
        onClearTags={() => setTagFilter(new Set())}
        viewMode={viewMode}
        onViewMode={setViewMode}
        onSelectMode={() => setSelectionMode((v) => !v)}
        selectionActive={selectionMode}
        findDupesVisible={showToolbarFindDuplicates}
        onFindDuplicates={handleFindDuplicates}
        dedupBusy={dedupBusy}
      />

      {records.length > 0 && (
        <CategoryBar
          records={records}
          value={categoryFilter}
          onChange={setCategoryFilter}
          caseId={caseId}
          onRecordUpdated={onRecordUpdated}
        />
      )}

      {search.trim().length >= 2 && (
        <div className="timeline-search-results" role="region" aria-label="Search results">
          {searchLoading && searchResults === null ? (
            <div className="timeline-search-status">Searching…</div>
          ) : !searchResults || searchResults.length === 0 ? (
            <div className="timeline-search-status">
              No records match “{search.trim()}”.
            </div>
          ) : (
            <>
              <div className="timeline-search-status">
                {searchResults.length} matching record
                {searchResults.length === 1 ? "" : "s"}
              </div>
              <ul className="timeline-search-list">
                {searchResults.map((res) => {
                  const meta = describedByRecordId.get(res.record_id);
                  const rec = recordsById.get(res.record_id);
                  const title =
                    meta?.title || rec?.subject || "(untitled record)";
                  return (
                    <li key={res.record_id}>
                      <button
                        type="button"
                        className="timeline-search-hit"
                        onClick={() =>
                          focusRecord(
                            res.record_id,
                            res.kind === "attachment"
                              ? res.attachment_id
                              : null,
                          )
                        }
                      >
                        <div className="timeline-search-hit-head">
                          <span className="timeline-search-hit-title">
                            {title}
                          </span>
                          {res.kind === "attachment" && (
                            <span className="timeline-search-hit-where">
                              📎 {res.attachment_filename || "attachment"}
                            </span>
                          )}
                          {res.trust === "low" && (
                            <span
                              className="timeline-search-hit-trust"
                              title="Match is in OCR / scanned text — verify against the original"
                            >
                              verify against original
                            </span>
                          )}
                        </div>
                        <div className="timeline-search-hit-snippet">
                          {renderSnippet(res.snippet)}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>
      )}

      {onlyFallbackDates && (
        <div
          className="timeline-fallback-banner"
          role="status"
          aria-live="polite"
        >
          <span>
            Showing records with no date found in content — the stored
            date is the filing time. Correct them by editing the
            record date.
          </span>
          <button
            type="button"
            onClick={() => onFallbackFilterHandled?.()}
          >
            Clear filter
          </button>
        </div>
      )}

      {anyFilterActive && (
        <div className="timeline-result-count">
          Showing {filteredCount} of {totalCount} records
        </div>
      )}

      {dedupMessage && (
        <div className="dedup-toast" role="status" aria-live="polite">
          <span>{dedupMessage}</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setDedupMessage(null)}
          >
            Dismiss
          </Button>
        </div>
      )}

      {selectionMode && (
        <SelectionBar
          filteredRecords={filteredRecords}
          selected={selected}
          onSelectAll={() => {
            const allIds = filteredRecords.map((r) => r.id);
            const everyIn = filteredRecords.every((r) => selected.has(r.id));
            if (everyIn) {
              setSelected(new Set());
            } else {
              setSelected(new Set(allIds));
            }
          }}
          onClear={() => setSelected(new Set())}
          onDelete={handleBulkDelete}
          onExit={() => {
            setSelectionMode(false);
            setSelected(new Set());
            setBulkError(null);
          }}
          bulkDeleting={bulkDeleting}
          bulkError={bulkError}
          showSelectAllDupes={showSelectAllDupes}
          duplicateCount={duplicateCount}
          onSelectDupes={selectAllDuplicates}
        />
      )}

      {filteredRecords.length === 0 ? (
        <div className="empty-state">
          <p>
            {records.length === 0
              ? "No records yet. Click + Add to capture your first one."
              : "No records match your filters."}
          </p>
        </div>
      ) : volumes.length > 1 ? (
        <VolumeAwareView
          caseId={caseId}
          volumes={volumes}
          showSummaryVolumeId={showSummaryVolumeId}
          onShowSummaryHandled={onShowSummaryHandled}
          orderedRecords={orderedRecords}
          viewMode={viewMode}
          groupByMonth={settings.records.groupByMonth}
          readingPage={readingPage}
          onReadingPage={setReadingPage}
          ctx={viewCtx}
          onAskAiAboutVolume={onAskAiAboutVolume}
          scrollToRecordId={scrollToRecordId}
        />
      ) : viewMode === "compact" ? (
        <CompactView
          orderedRecords={orderedRecords}
          ctx={viewCtx}
          groupByMonth={settings.records.groupByMonth}
        />
      ) : viewMode === "comfortable" ? (
        <ComfortableView
          orderedRecords={orderedRecords}
          ctx={viewCtx}
          groupByMonth={settings.records.groupByMonth}
        />
      ) : (
        <ReadingView
          orderedRecords={orderedRecords}
          ctx={viewCtx}
          page={readingPage}
          onPage={setReadingPage}
        />
      )}

      {!showRoutine && hiddenRoutineCount > 0 && (
        <div className="routine-banner" role="status" aria-live="polite">
          <div className="routine-banner-icon" aria-hidden>
            👁
          </div>
          <div className="routine-banner-text">
            <strong>
              {hiddenRoutineCount} routine record
              {hiddenRoutineCount === 1 ? "" : "s"} hidden
            </strong>
            <span className="routine-banner-sub">
              AI classified as low-importance and filtered out of the
              list. They still feed AI summaries and exports.
            </span>
          </div>
          <button
            type="button"
            className="routine-banner-action"
            onClick={() => onShowRoutineChange(true)}
          >
            Show hidden records
          </button>
        </div>
      )}

      {showRoutine && hiddenRoutineCount > 0 && (
        <div
          className="routine-banner routine-banner-shown"
          role="status"
          aria-live="polite"
        >
          <div className="routine-banner-icon" aria-hidden>
            👁
          </div>
          <div className="routine-banner-text">
            <strong>
              Showing all records ({hiddenRoutineCount} routine)
            </strong>
            <span className="routine-banner-sub">
              Routine records are visible. Hide them to focus on Key
              and Notable items.
            </span>
          </div>
          <button
            type="button"
            className="routine-banner-action"
            onClick={() => onShowRoutineChange(false)}
          >
            Hide routine records
          </button>
        </div>
      )}

      {lightbox && (
        <ZoomableLightbox
          src={lightbox.url}
          filename={lightbox.filename}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared view context — passed down to each row/card so they can hook
// into selection, expansion, and callbacks without re-threading every
// prop.
// ---------------------------------------------------------------------------
interface ViewContext {
  caseId: string;
  selectionMode: boolean;
  selected: Set<string>;
  expandedIds: Set<string>;
  // Inert plumbing: the date+subject record grouping was removed, so these
  // always carry empty maps and no grouping line is rendered. Kept on the
  // context so the view renderers keep typechecking unchanged.
  expandedClusters: Set<string>;
  toggleExpanded: (id: string) => void;
  toggleCluster: (leaderId: string) => void;
  duplicateClusters: Map<string, DuplicateCluster>;
  duplicateFollowerIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onToggleSelectMany: (ids: string[], on: boolean) => void;
  onSingleDelete: (id: string) => void;
  onDismissDuplicate: (id: string) => void;
  onRecordUpdated: (r: CaseRecord) => void;
  onOpenLightbox: (url: string, filename: string) => void;
  describedByRecordId: Map<string, ReturnType<typeof describeRecord>>;
  // When set, the matching row/card renders with a 2s amber flash
  // so the user's eye lands on the record a citation just navigated
  // them to. Null between flashes.
  flashingId: string | null;
  // When a search result deep-links to a specific attachment, its
  // AttachmentCard auto-opens the inline viewer. Null otherwise.
  focusAttachmentId: string | null;
}

// ---------------------------------------------------------------------------
// Toolbar
// ---------------------------------------------------------------------------

function TimelineToolbar({
  search,
  onSearch,
  typeFilter,
  onTypeFilter,
  sigFilter,
  onSigFilter,
  keyOnly,
  onToggleKeyOnly,
  showRoutine,
  hiddenRoutineCount,
  onToggleShowRoutine,
  allTags,
  tagFilter,
  onToggleTag,
  onClearTags,
  viewMode,
  onViewMode,
  onSelectMode,
  selectionActive,
  findDupesVisible,
  onFindDuplicates,
  dedupBusy,
}: {
  search: string;
  onSearch: (v: string) => void;
  typeFilter: TypeFilter;
  onTypeFilter: (v: TypeFilter) => void;
  sigFilter: SignificanceFilter;
  onSigFilter: (v: SignificanceFilter) => void;
  keyOnly: boolean;
  onToggleKeyOnly: () => void;
  showRoutine: boolean;
  hiddenRoutineCount: number;
  onToggleShowRoutine: () => void;
  allTags: string[];
  tagFilter: Set<string>;
  onToggleTag: (t: string) => void;
  onClearTags: () => void;
  viewMode: ViewMode;
  onViewMode: (v: ViewMode) => void;
  onSelectMode: () => void;
  selectionActive: boolean;
  findDupesVisible: boolean;
  onFindDuplicates: () => void;
  dedupBusy: boolean;
}) {
  const [tagsOpen, setTagsOpen] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const activeFilterCount =
    (typeFilter !== "all" ? 1 : 0) +
    (sigFilter !== "all" ? 1 : 0) +
    (keyOnly ? 1 : 0) +
    (showRoutine ? 1 : 0) +
    (tagFilter.size > 0 ? 1 : 0);
  return (
    <div
      className={`timeline-toolbar${
        mobileFiltersOpen ? " mobile-filters-open" : ""
      }`}
    >
      <div className="timeline-toolbar-filters">
        <Tooltip title="Search records by subject, sender, or content">
          <div className="timeline-search">
            <span className="timeline-search-icon" aria-hidden>
              🔍
            </span>
            <input
              type="search"
              className="timeline-search-input"
              placeholder="Search records…"
              value={search}
              onChange={(e) => onSearch(e.target.value)}
              aria-label="Search records"
            />
          </div>
        </Tooltip>

        <button
          type="button"
          className="timeline-filter-toggle"
          onClick={() => setMobileFiltersOpen((v) => !v)}
          aria-expanded={mobileFiltersOpen}
        >
          Filter{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
          <span aria-hidden>▾</span>
        </button>

        <div className="timeline-filter-collapsible">
          <Tooltip title="Filter by record type">
            <select
              className="timeline-filter-select"
              value={typeFilter}
              onChange={(e) => onTypeFilter(e.target.value as TypeFilter)}
              aria-label="Filter by type"
            >
              <option value="all">All types</option>
              <option value="email">Email</option>
              <option value="screenshot">Screenshot</option>
              <option value="video">Video</option>
              <option value="audio">Audio</option>
              <option value="file">File</option>
              <option value="other">Other</option>
            </select>
          </Tooltip>

          <Tooltip title="Filter by AI significance score">
            <select
              className="timeline-filter-select"
              value={sigFilter}
              onChange={(e) =>
                onSigFilter(e.target.value as SignificanceFilter)
              }
              aria-label="Filter by significance"
            >
              <option value="all">All significance</option>
              <option value="high">🔴 Key</option>
              <option value="medium">🟡 Notable</option>
              <option value="low">⚪ Routine</option>
            </select>
          </Tooltip>

          {allTags.length > 0 && (
            <div className="timeline-tag-wrap">
              <Tooltip title="Filter by tag">
                <Button
                  variant={tagFilter.size > 0 ? "primary" : "secondary"}
                  size="md"
                  onClick={() => setTagsOpen((v) => !v)}
                  trailing={<span aria-hidden>▾</span>}
                >
                  Tags{tagFilter.size > 0 ? ` (${tagFilter.size})` : ""}
                </Button>
              </Tooltip>
              {tagsOpen && (
                <div className="timeline-tag-menu" role="menu">
                  {allTags.map((t) => (
                    <label key={t} className="timeline-tag-menu-item">
                      <input
                        type="checkbox"
                        checked={tagFilter.has(t)}
                        onChange={() => onToggleTag(t)}
                      />
                      <span>{t}</span>
                    </label>
                  ))}
                  {tagFilter.size > 0 && (
                    <button
                      type="button"
                      className="timeline-tag-menu-clear"
                      onClick={() => {
                        onClearTags();
                        setTagsOpen(false);
                      }}
                    >
                      Clear all
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          <Tooltip title="Show only high significance records">
            <Button
              variant={keyOnly ? "secondary" : "ghost"}
              size="md"
              onClick={onToggleKeyOnly}
              className={`key-only-toggle${keyOnly ? " active" : ""}`}
              aria-pressed={keyOnly}
            >
              Key only
            </Button>
          </Tooltip>

          <Tooltip
            title={
              showRoutine
                ? "Hide Routine records to focus on Key and Notable. Routine records still appear in AI summaries and exports."
                : "Include Routine records in the list. Currently hidden — AI summaries and exports still see them."
            }
          >
            <Button
              variant={showRoutine ? "primary" : "ghost"}
              size="md"
              onClick={onToggleShowRoutine}
              className={`show-routine-toggle${showRoutine ? " active" : " inactive"}`}
              aria-pressed={showRoutine}
            >
              {showRoutine
                ? "Hide routine"
                : hiddenRoutineCount > 0
                  ? `Show routine (${hiddenRoutineCount})`
                  : "Show routine"}
            </Button>
          </Tooltip>
        </div>
      </div>

      <div className="timeline-toolbar-actions">
        {findDupesVisible && (
          <Tooltip title="Scan for duplicate records and flag them for review">
            <Button
              variant="ghost"
              size="md"
              onClick={onFindDuplicates}
              disabled={dedupBusy}
            >
              {dedupBusy ? "Scanning…" : "Find duplicates"}
            </Button>
          </Tooltip>
        )}
        <Tooltip title="Select records for bulk actions">
          <Button
            variant={selectionActive ? "primary" : "ghost"}
            size="md"
            onClick={onSelectMode}
          >
            {selectionActive ? "Done" : "Select"}
          </Button>
        </Tooltip>

        <div
          className="timeline-view-switcher"
          role="radiogroup"
          aria-label="View mode"
        >
          {(["compact", "comfortable", "reading"] as ViewMode[]).map((m) => (
            <Tooltip
              key={m}
              title={
                m === "compact"
                  ? "Compact table view — most records visible at once"
                  : m === "comfortable"
                    ? "Card view with content preview"
                    : "Full text view for reading through the case"
              }
            >
              <button
                type="button"
                role="radio"
                aria-checked={viewMode === m}
                className={`timeline-view-btn${
                  viewMode === m ? " active" : ""
                }`}
                onClick={() => onViewMode(m)}
              >
                {m === "compact"
                  ? "Compact"
                  : m === "comfortable"
                    ? "Comfortable"
                    : "Reading"}
              </button>
            </Tooltip>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Selection bar — shown when selectionMode is active
// ---------------------------------------------------------------------------
function SelectionBar({
  filteredRecords,
  selected,
  onSelectAll,
  onClear,
  onDelete,
  onExit,
  bulkDeleting,
  bulkError,
  showSelectAllDupes,
  duplicateCount,
  onSelectDupes,
}: {
  filteredRecords: CaseRecord[];
  selected: Set<string>;
  onSelectAll: () => void;
  onClear: () => void;
  onDelete: () => void;
  onExit: () => void;
  bulkDeleting: boolean;
  bulkError: string | null;
  showSelectAllDupes: boolean;
  duplicateCount: number;
  onSelectDupes: () => void;
}) {
  const all = filteredRecords.every((r) => selected.has(r.id));
  const some = selected.size > 0;
  return (
    <div className={`records-selection-bar${some ? " active" : ""}`}>
      <label className="records-select-all">
        <input
          type="checkbox"
          checked={all && filteredRecords.length > 0}
          onChange={onSelectAll}
          ref={(el) => {
            if (el) el.indeterminate = some && !all;
          }}
          aria-label="Select all visible records"
        />
        {some
          ? `${selected.size} selected`
          : `Select all (${filteredRecords.length})`}
      </label>
      {showSelectAllDupes && (
        <Button variant="ghost" size="sm" onClick={onSelectDupes}>
          Select all duplicates ({duplicateCount})
        </Button>
      )}
      {some && (
        <div className="records-selection-actions">
          {bulkError && <span className="form-error">{bulkError}</span>}
          <Button
            variant="ghost"
            size="sm"
            onClick={onClear}
            disabled={bulkDeleting}
          >
            Clear
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={onDelete}
            disabled={bulkDeleting}
          >
            {bulkDeleting ? "Deleting…" : `Delete (${selected.size})`}
          </Button>
        </div>
      )}
      <Button variant="ghost" size="sm" onClick={onExit}>
        Exit
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// COMPACT VIEW
// ---------------------------------------------------------------------------

function CompactView({
  orderedRecords,
  ctx,
  groupByMonth,
}: {
  orderedRecords: { pinned: CaseRecord[]; unpinned: CaseRecord[] };
  ctx: ViewContext;
  groupByMonth: boolean;
}) {
  // 6 fixed columns: DATE, TYPE, FROM, SUBJECT, ATTACH, SIG. Selection
  // mode prepends a checkbox column.
  const colCount = (ctx.selectionMode ? 1 : 0) + 6;

  const out: React.ReactNode[] = [];

  if (orderedRecords.pinned.length > 0) {
    out.push(
      <tr key="h-pinned" className="records-month-header-row">
        <td colSpan={colCount}>📌 Pinned</td>
      </tr>,
    );
    for (const record of orderedRecords.pinned) {
      out.push(<CompactRow key={record.id} record={record} ctx={ctx} />);
    }
  }

  // id → record lookup so expanded-cluster follower rows can be
  // rendered even when they aren't contiguous with the leader in
  // the canonical sort order.
  const byId = new Map<string, CaseRecord>();
  for (const r of orderedRecords.unpinned) byId.set(r.id, r);

  let currentMonth = "";
  for (let i = 0; i < orderedRecords.unpinned.length; i++) {
    const record = orderedRecords.unpinned[i]!;

    if (groupByMonth) {
      const label = monthYearLabel(record.record_date);
      if (label !== currentMonth) {
        out.push(
          <tr
            key={`h-${label}-${i}`}
            className="records-month-header-row"
          >
            <td colSpan={colCount}>{label}</td>
          </tr>,
        );
        currentMonth = label;
      }
    }

    out.push(<CompactRow key={record.id} record={record} ctx={ctx} />);
  }

  return (
    <div className="compact-view">
      <table className="compact-table">
        <colgroup>
          {ctx.selectionMode && <col style={{ width: "28px" }} />}
          <col style={{ width: "120px" }} />
          <col style={{ width: "120px" }} />
          <col className="compact-col-from" style={{ width: "160px" }} />
          <col />
          <col style={{ width: "60px" }} />
          <col style={{ width: "50px" }} />
        </colgroup>
        <thead>
          <tr className="compact-header-row">
            {ctx.selectionMode && <th aria-hidden />}
            <th className="compact-col-date">Date</th>
            <th className="compact-col-type">Type</th>
            <th className="compact-col-from">From</th>
            <th className="compact-col-subject">Subject / Title</th>
            <th className="compact-col-attach" aria-label="Attachments">
              <Tooltip title="Attachments">
                <span className="compact-col-attach-icon" aria-hidden>
                  📎
                </span>
              </Tooltip>
            </th>
            <th className="compact-col-sig" aria-label="Importance">
              <Tooltip title="Importance">
                <span className="compact-col-sig-icon" aria-hidden>
                  ◆
                </span>
              </Tooltip>
            </th>
          </tr>
        </thead>
        <tbody>{out}</tbody>
      </table>
    </div>
  );
}

function CompactRow({
  record,
  ctx,
}: {
  record: CaseRecord;
  ctx: ViewContext;
}) {
  const meta = ctx.describedByRecordId.get(record.id);
  const expanded = ctx.expandedIds.has(record.id);
  const selected = ctx.selected.has(record.id);
  const category = meta?.category ?? "other";
  const showPeople = meta?.showPeopleColumns ?? false;
  const colCount = (ctx.selectionMode ? 1 : 0) + 6;
  // Records inside a duplicate cluster (leader or follower) drop the
  // "Possible duplicate" strip and inline ⚠️ — the cluster line
  // already communicates the duplicate-ness. Keep the strip for
  // solo is_duplicate=1 records that didn't form a cluster (e.g.
  // just a pair of similars).
  const inCluster =
    ctx.duplicateClusters.has(record.id) ||
    ctx.duplicateFollowerIds.has(record.id);
  const showDuplicateUi = false; // duplicate banners removed
  const auditFlagged = isAuditFlagged(record.extraction_audit);

  const rowClass = [
    "compact-row",
    expanded ? "expanded" : "",
    selected ? "selected" : "",
    showDuplicateUi ? "is-duplicate" : "",
    ctx.selectionMode ? "selection-mode" : "",
    ctx.flashingId === record.id ? "record-flash" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      {showDuplicateUi && (
        <tr className="compact-strip-row">
          <td colSpan={colCount}>
            <DuplicateStrip
              onDismiss={() => ctx.onDismissDuplicate(record.id)}
              onDelete={() => ctx.onSingleDelete(record.id)}
            />
          </td>
        </tr>
      )}
      <tr
        id={`record-${record.id}`}
        className={rowClass}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={() => ctx.toggleExpanded(record.id)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            ctx.toggleExpanded(record.id);
          }
        }}
      >
        {ctx.selectionMode && (
          <td
            className="compact-col-check"
            onClick={(e) => e.stopPropagation()}
          >
            <input
              type="checkbox"
              checked={selected}
              onChange={() => ctx.onToggleSelect(record.id)}
              aria-label="Select record"
            />
          </td>
        )}
        <td className="compact-col-date">
          {formatShortDate(record.record_date)}
          <span className="compact-col-time">
            {" · "}
            {formatTime(record.record_date) || formatTime(record.logged_at)}
          </span>
        </td>
        <td className="compact-col-type">
          <Badge tone="neutral" size="compact" icon={meta?.typeEmoji}>
            {meta?.typeLabel ?? "Other"}
          </Badge>
          {(() => {
            const cm = categoryMeta(effectiveCategory(record));
            return (
              <span
                className={`cat-chip-mini cat-${cm.id}`}
                title={`Category: ${cm.label}`}
                aria-label={`Category: ${cm.label}`}
              >
                {cm.emoji}
              </span>
            );
          })()}
        </td>
        {showPeople ? (
          <>
            <td className="compact-col-from" title={record.from_addr ?? ""}>
              {truncate(record.from_addr, 40) || "—"}
            </td>
            <td className="compact-col-subject">
              {showDuplicateUi ? (
                <span
                  className="compact-dupe-badge"
                  title="Flagged as a possible duplicate"
                  aria-label="Possible duplicate"
                >
                  ⚠️
                </span>
              ) : null}
              {record.pinned ? <span aria-hidden>📌 </span> : null}
              {record.is_edited === 1 ? <span aria-hidden>✏️ </span> : null}
              {auditFlagged ? (<span className="audit-flag-icon" title="Extraction audit flagged — expand to review" aria-label="Extraction audit flagged">⚑ </span>) : null}<span title={meta?.subtitle || undefined}>{meta?.title ?? "(untitled)"}</span>
            </td>
          </>
        ) : (
          <td className="compact-col-subject" colSpan={2}>
            {showDuplicateUi ? (
              <span
                className="compact-dupe-badge"
                title="Flagged as a possible duplicate"
                aria-label="Possible duplicate"
              >
                ⚠️
              </span>
            ) : null}
            {record.pinned ? <span aria-hidden>📌 </span> : null}
            {record.is_edited === 1 ? <span aria-hidden>✏️ </span> : null}
            {auditFlagged ? (<span className="audit-flag-icon" title="Extraction audit flagged — expand to review" aria-label="Extraction audit flagged">⚑ </span>) : null}<span title={meta?.subtitle || undefined}>{meta?.title ?? "(untitled)"}</span>
          </td>
        )}
        <td className="compact-col-attach" aria-label="Attachments">
          <AttachmentInlinePreview record={record} ctx={ctx} />
        </td>
        <td className="compact-col-sig">
          <Badge
            tone={sigTone(record.significance)}
            size="compact"
            title={
              record.significance_reason ??
              SIGNIFICANCE_LABELS[record.significance]
            }
          >
            {SIGNIFICANCE_EMOJI[record.significance]}
          </Badge>
        </td>
      </tr>
      {expanded && (
        <tr className="compact-expansion-row">
          <td colSpan={colCount}>
            <div className="compact-expansion">
              <RecordExpanded record={record} ctx={ctx} category={category} />
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// Tiny inline attachment preview: 📎 N, or 32px thumb for 1-2 images.
function AttachmentInlinePreview({
  record,
  ctx,
}: {
  record: CaseRecord;
  ctx: ViewContext;
}) {
  const atts = record.attachments ?? [];
  if (atts.length === 0) return null;
  const images = atts.filter(
    (a) => attachmentKind(a.mime_type) === "image",
  );
  if (images.length > 0 && images.length <= 2) {
    return (
      <span className="compact-thumb-row">
        {images.slice(0, 2).map((img) => (
          <button
            key={img.id}
            type="button"
            className="compact-thumb-btn"
            onClick={(e) => {
              e.stopPropagation();
              ctx.onOpenLightbox(fileUrl(ctx.caseId, img.r2_key), img.filename);
            }}
            aria-label={`Open ${img.filename}`}
          >
            <AuthImage
              src={fileUrl(ctx.caseId, img.r2_key)}
              alt=""
              className="compact-thumb"
              loading="lazy"
            />
          </button>
        ))}
        {atts.length > images.length && (
          <span className="compact-attach-extra">+{atts.length - images.length}</span>
        )}
      </span>
    );
  }
  return (
    <Tooltip title="View attachments">
      <span
        className="compact-attach-count"
        role="img"
        aria-label={`${atts.length} attachment${atts.length === 1 ? "" : "s"}`}
      >
        📎 {atts.length}
      </span>
    </Tooltip>
  );
}

function DuplicateStrip({
  onDismiss,
  onDelete,
}: {
  onDismiss: () => void;
  onDelete: () => void;
}) {
  return (
    <div
      className="duplicate-badge"
      onClick={(e) => e.stopPropagation()}
      role="note"
    >
      <span className="duplicate-badge-label">
        <span aria-hidden>⚠️</span> Possible duplicate
      </span>
      <div className="duplicate-badge-actions">
        <Button variant="ghost" size="sm" onClick={onDismiss}>
          Dismiss
        </Button>
        <Button variant="danger" size="sm" onClick={onDelete}>
          Delete
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// COMFORTABLE VIEW
// ---------------------------------------------------------------------------
function ComfortableView({
  orderedRecords,
  ctx,
  groupByMonth,
}: {
  orderedRecords: { pinned: CaseRecord[]; unpinned: CaseRecord[] };
  ctx: ViewContext;
  groupByMonth: boolean;
}) {
  const out: React.ReactNode[] = [];

  if (orderedRecords.pinned.length > 0) {
    out.push(
      <li key="h-pinned" className="records-month-header">
        📌 Pinned
      </li>,
    );
    for (const record of orderedRecords.pinned) {
      out.push(
        <ComfortableCard key={record.id} record={record} ctx={ctx} />,
      );
    }
  }

  const byId = new Map<string, CaseRecord>();
  for (const r of orderedRecords.unpinned) byId.set(r.id, r);

  let currentMonth = "";
  for (let i = 0; i < orderedRecords.unpinned.length; i++) {
    const record = orderedRecords.unpinned[i]!;

    if (groupByMonth) {
      const label = monthYearLabel(record.record_date);
      if (label !== currentMonth) {
        out.push(
          <li key={`h-${label}-${i}`} className="records-month-header">
            {label}
          </li>,
        );
        currentMonth = label;
      }
    }

    out.push(<ComfortableCard key={record.id} record={record} ctx={ctx} />);
  }

  return <ol className="comfortable-list">{out}</ol>;
}

function ComfortableCard({
  record,
  ctx,
}: {
  record: CaseRecord;
  ctx: ViewContext;
}) {
  const meta = ctx.describedByRecordId.get(record.id);
  const expanded = ctx.expandedIds.has(record.id);
  const selected = ctx.selected.has(record.id);
  const preview = bodyPreview(record, 220);
  const attachments = record.attachments ?? [];
  const images = attachments.filter(
    (a) => attachmentKind(a.mime_type) === "image",
  );
  const isScreenshot = meta?.category === "screenshot";
  const confidence = parseExtractionConfidence(record.extraction_confidence);
  const dateIsFallback = confidence?.date === "fallback";
  const inCluster =
    ctx.duplicateClusters.has(record.id) ||
    ctx.duplicateFollowerIds.has(record.id);
  const showDuplicateUi = false; // duplicate banners removed

  return (
    <li
      id={`record-${record.id}`}
      className={`comfort-card-wrap${expanded ? " expanded" : ""}${
        selected ? " selected" : ""
      }${showDuplicateUi ? " is-duplicate" : ""}${
        ctx.flashingId === record.id ? " record-flash" : ""
      }`}
    >
      {showDuplicateUi && (
        <DuplicateStrip
          onDismiss={() => ctx.onDismissDuplicate(record.id)}
          onDelete={() => ctx.onSingleDelete(record.id)}
        />
      )}
      <div
        className="comfort-card"
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={() => ctx.toggleExpanded(record.id)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            ctx.toggleExpanded(record.id);
          }
        }}
      >
        {ctx.selectionMode && (
          <label
            className="comfort-card-check"
            onClick={(e) => e.stopPropagation()}
          >
            <input
              type="checkbox"
              checked={selected}
              onChange={() => ctx.onToggleSelect(record.id)}
              aria-label="Select record"
            />
          </label>
        )}
        <div className="comfort-card-head">
          <Badge tone="neutral" size="standard" icon={meta?.typeEmoji}>
            {meta?.typeLabel ?? "Other"}
          </Badge>
          <Badge
            tone={sigTone(record.significance)}
            size="standard"
            title={record.significance_reason ?? SIGNIFICANCE_LABELS[record.significance]}
          >
            {SIGNIFICANCE_EMOJI[record.significance]}{" "}
            {SIGNIFICANCE_LABELS[record.significance]}
          </Badge>
          {record.pinned === 1 && (
            <Badge tone="accent" size="standard" icon="📌">
              Pinned
            </Badge>
          )}
          {record.is_edited === 1 && (
            <Badge tone="info" size="standard" icon="✏️">
              Edited
            </Badge>
          )}
          <span
            className={`comfort-card-date${dateIsFallback ? " date-fallback" : ""}`}
          >
            {formatShortDate(record.record_date)}
            {formatTime(record.record_date) && (
              <> · {formatTime(record.record_date)}</>
            )}
            <ConfidenceDot confidence={confidence} field="date" />
          </span>
        </div>
        <div className="comfort-card-title">
          <span title={meta?.subtitle || undefined}>{meta?.title ?? "(untitled)"}</span>
          <ConfidenceDot
            confidence={confidence}
            field="subject"
            hidden={!record.subject}
          />
        </div>
        {meta?.subtitle && (
          <div className="comfort-card-subtitle" title={meta.subtitle}>
            {meta.subtitle}
          </div>
        )}
        {meta?.showPeopleColumns && (record.from_addr || record.to_addr) && (
          <div className="comfort-card-people">
            {record.from_addr && (
              <span>
                <strong>From:</strong> {truncate(record.from_addr, 60)}
                <ConfidenceDot confidence={confidence} field="from" />
              </span>
            )}
            {record.to_addr && (
              <span>
                <strong>To:</strong> {truncate(record.to_addr, 60)}
                <ConfidenceDot confidence={confidence} field="to" />
              </span>
            )}
          </div>
        )}
        {isScreenshot && images.length > 0 && (
          <button
            type="button"
            className="comfort-card-hero-thumb-btn"
            onClick={(e) => {
              e.stopPropagation();
              const img = images[0]!;
              ctx.onOpenLightbox(
                fileUrl(ctx.caseId, img.r2_key),
                img.filename,
              );
            }}
            aria-label={`Open ${images[0]!.filename}`}
          >
            <AuthImage
              src={fileUrl(ctx.caseId, images[0]!.r2_key)}
              alt=""
              className="comfort-card-hero-thumb"
              loading="lazy"
            />
          </button>
        )}
        {preview && (
          <div className="comfort-card-preview">{preview}</div>
        )}
        {attachments.length > 0 && !expanded && (
          <div className="comfort-card-attach-hint">
            📎 {attachments.length} attachment
            {attachments.length === 1 ? "" : "s"}
          </div>
        )}
      </div>
      {expanded && (
        <div className="comfort-expansion">
          <RecordExpanded
            record={record}
            ctx={ctx}
            category={meta?.category ?? "other"}
          />
        </div>
      )}
    </li>
  );
}

// ---------------------------------------------------------------------------
// READING VIEW
// ---------------------------------------------------------------------------
function ReadingView({
  orderedRecords,
  ctx,
  page,
  onPage,
}: {
  orderedRecords: { pinned: CaseRecord[]; unpinned: CaseRecord[] };
  ctx: ViewContext;
  page: number;
  onPage: (n: number) => void;
}) {
  // Reading mode shows every record in canonical order. Duplicate
  // clusters still honour the display-only collapse so the user
  // doesn't read through ten near-identical copies; pagination
  // treats the leader, cluster line, and any expanded followers as
  // separate items so the page boundary is predictable.
  const flat: Array<{ kind: "record"; record: CaseRecord }> = [];
  for (const r of orderedRecords.pinned) flat.push({ kind: "record", record: r });
  for (const r of orderedRecords.unpinned)
    flat.push({ kind: "record", record: r });

  const totalPages = Math.max(1, Math.ceil(flat.length / PAGE_SIZE_READING));
  const start = page * PAGE_SIZE_READING;
  const slice = flat.slice(start, start + PAGE_SIZE_READING);
  return (
    <div className="reading-view">
      <ol className="reading-list">
        {slice.map((item) => (
          <ReadingBlock key={item.record.id} record={item.record} ctx={ctx} />
        ))}
      </ol>
      <div className="reading-pagination">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => onPage(Math.max(0, page - 1))}
          disabled={page === 0}
        >
          Previous
        </Button>
        <span className="reading-page-info">
          Page {page + 1} of {totalPages}
        </span>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => onPage(Math.min(totalPages - 1, page + 1))}
          disabled={page + 1 >= totalPages}
        >
          Next
        </Button>
      </div>
    </div>
  );
}

function ReadingBlock({
  record,
  ctx,
}: {
  record: CaseRecord;
  ctx: ViewContext;
}) {
  const meta = ctx.describedByRecordId.get(record.id);
  const confidence = parseExtractionConfidence(record.extraction_confidence);
  const dateIsFallback = confidence?.date === "fallback";
  const inCluster =
    ctx.duplicateClusters.has(record.id) ||
    ctx.duplicateFollowerIds.has(record.id);
  const showDuplicateUi = false; // duplicate banners removed
  return (
    <li
      id={`record-${record.id}`}
      className={`reading-block${showDuplicateUi ? " is-duplicate" : ""}${
        ctx.flashingId === record.id ? " record-flash" : ""
      }`}
    >
      {showDuplicateUi && (
        <DuplicateStrip
          onDismiss={() => ctx.onDismissDuplicate(record.id)}
          onDelete={() => ctx.onSingleDelete(record.id)}
        />
      )}
      <div className="reading-block-head">
        <Badge tone="neutral" size="standard" icon={meta?.typeEmoji}>
          {meta?.typeLabel ?? "Other"}
        </Badge>
        <Badge
          tone={sigTone(record.significance)}
          size="standard"
          title={record.significance_reason ?? undefined}
        >
          {SIGNIFICANCE_EMOJI[record.significance]}{" "}
          {SIGNIFICANCE_LABELS[record.significance]}
        </Badge>
        <span
          className={`reading-block-date${dateIsFallback ? " date-fallback" : ""}`}
        >
          {formatShortDate(record.record_date)}
          <ConfidenceDot confidence={confidence} field="date" />
        </span>
      </div>
      <div className="reading-block-title">
        {meta?.title}
        <ConfidenceDot
          confidence={confidence}
          field="subject"
          hidden={!record.subject}
        />
      </div>
      <RecordExpanded
        record={record}
        ctx={ctx}
        category={meta?.category ?? "other"}
      />
    </li>
  );
}

// ---------------------------------------------------------------------------
// RecordExpanded — shared layout across all three view modes
// ---------------------------------------------------------------------------
function RecordExpanded({
  record,
  ctx,
  category,
}: {
  record: CaseRecord;
  ctx: ViewContext;
  category: RecordCategory;
}) {
  const [pinBusy, setPinBusy] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [metaOpen, setMetaOpen] = useState(false);
  const [diffOpen, setDiffOpen] = useState(false);
  const [reocrBusy, setReocrBusy] = useState(false);
  const [retranscribeBusy, setRetranscribeBusy] = useState(false);
  // Local zoomable-lightbox state for the record's own
  // file_r2_key image (not attachments children, which have their
  // own lightbox via AttachmentCard + ctx.onOpenLightbox).
  const [primaryLightboxUrl, setPrimaryLightboxUrl] = useState<
    string | null
  >(null);

  async function rerunOcr() {
    setReocrBusy(true);
    try {
      const res = await api.rerunOcr(record.id);
      ctx.onRecordUpdated(res.record);
      if (res.ocr_result.extracted_empty) {
        window.alert(
          "Finished reading the image, but no text could be extracted. " +
            "It may be blank, very low contrast, or have no readable text.",
        );
      }
    } catch (err) {
      console.error("[reocr] failed:", err);
      window.alert(`Re-read image failed: ${(err as Error).message}`);
    } finally {
      setReocrBusy(false);
    }
  }

  const isImageRecord =
    category === "screenshot" ||
    (record.file_mime_type ?? "").startsWith("image/");
  const canRerunOcr = isImageRecord && !!record.file_r2_key;

  const isAudioRecord = (record.file_mime_type ?? "").startsWith("audio/");
  const canRerunTranscribe = isAudioRecord && !!record.file_r2_key;

  async function rerunTranscribe() {
    setRetranscribeBusy(true);
    try {
      const res = await api.rerunTranscribe(record.id);
      ctx.onRecordUpdated(res.record);
      if (res.transcript_result.extracted_empty) {
        window.alert(
          "Finished transcribing, but no speech could be extracted. " +
            "The audio may be silent or too quiet.",
        );
      }
    } catch (err) {
      console.error("[retranscribe] failed:", err);
      window.alert(`Re-transcribe failed: ${(err as Error).message}`);
    } finally {
      setRetranscribeBusy(false);
    }
  }

  async function togglePin() {
    setPinBusy(true);
    try {
      const { record: next } = await api.updateRecord(record.id, {
        pinned: !record.pinned,
      });
      ctx.onRecordUpdated(next);
    } catch (err) {
      window.alert(`Pin failed: ${(err as Error).message}`);
    } finally {
      setPinBusy(false);
    }
  }

  const showPeople = category === "email" || category === "letter";
  const attachments = record.attachments ?? [];
  const confidence = parseExtractionConfidence(record.extraction_confidence);
  const dateIsFallback = confidence?.date === "fallback";

  return (
    <div className="record-expanded">
      {/* Header metadata block */}
      <dl className="record-expanded-meta">
        {showPeople && (
          <>
            <dt>From</dt>
            <dd>
              {record.from_addr ?? "—"}
              {record.from_addr && (
                <ConfidenceDot confidence={confidence} field="from" />
              )}
            </dd>
            <dt>To</dt>
            <dd>
              {record.to_addr ?? "—"}
              {record.to_addr && (
                <ConfidenceDot confidence={confidence} field="to" />
              )}
            </dd>
          </>
        )}
        {record.subject && (
          <>
            <dt>Subject</dt>
            <dd>
              {record.subject}
              <ConfidenceDot confidence={confidence} field="subject" />
            </dd>
          </>
        )}
        <dt>Date</dt>
        <dd className={dateIsFallback ? "date-fallback" : undefined}>
          {formatDateTime(record.record_date)}
          <ConfidenceDot confidence={confidence} field="date" />
          <DateSourceLabel source={record.date_source ?? null} />
        </dd>
        <dt>Added</dt>
        <dd>{formatDateTime(record.logged_at)}</dd>
        {record.forwarded_via && (
          <>
            <dt>Forwarded via</dt>
            <dd>{record.forwarded_via}</dd>
          </>
        )}
        {record.significance_reason && (
          <>
            <dt>
              {SIGNIFICANCE_EMOJI[record.significance]}{" "}
              {SIGNIFICANCE_LABELS[record.significance]}
            </dt>
            <dd>{record.significance_reason}</dd>
          </>
        )}
      </dl>

      <ExtractionAuditSection record={record} />

      <ReceiptDetected record={record} />

      <div className="record-category-row">
        <span className="record-category-label">Category</span>
        <CategoryBadge
          record={record}
          caseId={ctx.caseId}
          onUpdated={ctx.onRecordUpdated}
        />
      </div>

      <TagEditor record={record} onRecordUpdated={ctx.onRecordUpdated} />

      <div className="record-expanded-actions">
        <Tooltip title="Pin this record to the top of the case">
          <Button
            variant="secondary"
            size="sm"
            onClick={togglePin}
            disabled={pinBusy}
          >
            {record.pinned ? "📌 Unpin" : "📌 Pin"}
          </Button>
        </Tooltip>
        <Tooltip title="Edit the record content">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setEditing((v) => !v)}
          >
            {editing ? "Stop editing" : "✏️ Edit"}
          </Button>
        </Tooltip>
        <Tooltip title="View and restore previous versions of this record">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setHistoryOpen(true)}
          >
            🕘 Version history
          </Button>
        </Tooltip>
        {canRerunOcr && (
          <Tooltip title="Ask the AI to re-read the text in this image">
            <Button
              variant="secondary"
              size="sm"
              onClick={rerunOcr}
              disabled={reocrBusy}
            >
              {reocrBusy ? "Re-reading image…" : "🔍 Re-read image"}
            </Button>
          </Tooltip>
        )}
        {canRerunTranscribe && (
          <Tooltip title="Re-transcribe the audio (uses OpenAI Whisper for long recordings)">
            <Button
              variant="secondary"
              size="sm"
              onClick={rerunTranscribe}
              disabled={retranscribeBusy}
            >
              {retranscribeBusy ? "Re-transcribing…" : "🎙 Re-transcribe"}
            </Button>
          </Tooltip>
        )}
        {record.is_edited === 1 && record.source_content && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setDiffOpen((v) => !v)}
          >
            {diffOpen ? "Hide original" : "Show original"}
          </Button>
        )}
        <Tooltip title="Delete this record permanently">
          <Button
            variant="danger"
            size="sm"
            onClick={() => ctx.onSingleDelete(record.id)}
          >
            Delete
          </Button>
        </Tooltip>
      </div>

      {diffOpen && record.source_content && (
        <div className="record-diff">
          <div className="record-diff-col">
            <h4>Original — immutable</h4>
            <pre className="body-text">{record.source_content}</pre>
          </div>
          <div className="record-diff-col">
            <h4>Current working copy</h4>
            <pre className="body-text">{record.body_text}</pre>
          </div>
        </div>
      )}

      {editing ? (
        <RecordEditForm
          record={record}
          onCancel={() => setEditing(false)}
          onSaved={(next) => {
            ctx.onRecordUpdated(next);
            setEditing(false);
          }}
        />
      ) : record.body_text ? (
        <RecordBodyWithAnnotations
          recordId={record.id}
          body={record.body_text}
        />
      ) : record.ocr_text ? (
        <>
          <h4>Text read from image</h4>
          <pre className="body-text">{record.ocr_text}</pre>
        </>
      ) : (
        <p className="muted">(no body content)</p>
      )}

      {record.notes && (
        <>
          <h4>Notes</h4>
          <pre className="body-text">{record.notes}</pre>
        </>
      )}

      {/* Primary-file attachment inline renderer — for records that
          store the file directly on the row (file_r2_key) rather
          than as an `attachments` table child. Covers paste-canvas
          screenshots, file-upload images/docs, and manually-written
          records with an attached file. Images preview inline and
          open the zoomable lightbox; other file types show a
          download link with a mime-appropriate icon. */}
      {record.file_r2_key && (
        <>
          <h4>Attachment</h4>
          {(record.file_mime_type ?? "").startsWith("image/") ? (
            <div className="record-attach-image-wrap">
              <button
                type="button"
                className="record-attach-image-btn"
                onClick={() =>
                  setPrimaryLightboxUrl(
                    fileUrl(ctx.caseId, record.file_r2_key!),
                  )
                }
                aria-label="Enlarge image"
                title="Click to enlarge"
              >
                <AuthImage
                  src={fileUrl(ctx.caseId, record.file_r2_key)}
                  alt={record.file_name ?? ""}
                  className="record-attach-image"
                  loading="lazy"
                />
              </button>
              {record.file_name && (
                <div className="record-attach-filename">
                  {record.file_name}
                </div>
              )}
            </div>
          ) : (
            <div className="record-attach-file">
              <span className="record-attach-file-icon" aria-hidden>
                {(() => {
                  const mime = record.file_mime_type ?? "";
                  if (mime === "application/pdf") return "📄";
                  if (mime.includes("wordprocessingml")) return "📝";
                  if (mime.includes("spreadsheetml")) return "📊";
                  if (mime.startsWith("audio/")) return "🎙️";
                  return "📎";
                })()}
              </span>
              <span className="record-attach-file-name">
                {record.file_name ?? "Attachment"}
              </span>
              <button
                type="button"
                className="record-attach-file-download"
                onClick={async () => {
                  // Bearer-aware download: a bare <a href> can't
                  // attach the Clerk JWT, so we fetch the blob
                  // through `request()`'s auth path, wrap in an
                  // object URL, then trigger a synthetic click on a
                  // throwaway <a download>. Revoked right after.
                  try {
                    const headers = await getAuthHeaders();
                    const res = await fetch(
                      fileUrl(ctx.caseId, record.file_r2_key!),
                      { credentials: "include", headers },
                    );
                    if (!res.ok) throw new Error(`http_${res.status}`);
                    const blob = await res.blob();
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = record.file_name ?? "download";
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    setTimeout(() => URL.revokeObjectURL(url), 1000);
                  } catch (err) {
                    window.alert(
                      `Couldn't download: ${(err as Error).message}`,
                    );
                  }
                }}
              >
                Download
              </button>
            </div>
          )}
        </>
      )}

      {primaryLightboxUrl && (
        <ZoomableLightbox
          src={primaryLightboxUrl}
          onClose={() => setPrimaryLightboxUrl(null)}
        />
      )}

      {attachments.length > 0 && (
        <>
          <h4>Attachments ({attachments.length})</h4>
          <div className="attach-card-list">
            {attachments.map((a) => (
              <AttachmentCard
                key={a.id}
                caseId={ctx.caseId}
                attachment={a}
                autoExpand={ctx.focusAttachmentId === a.id}
                onOpenLightbox={(url, filename) =>
                  ctx.onOpenLightbox(url, filename)
                }
              />
            ))}
          </div>
        </>
      )}

      {/* Collapsible metadata footer */}
      <div className="record-expanded-meta-footer">
        <button
          type="button"
          className="record-expanded-meta-toggle"
          onClick={() => setMetaOpen((v) => !v)}
        >
          {metaOpen ? "▾" : "▸"} Metadata
        </button>
        {metaOpen && (
          <dl className="record-expanded-meta">
            <dt>Record ID</dt>
            <dd>
              <code>{record.id}</code>
            </dd>
            {record.source_hash && (
              <>
                <dt>Source hash (SHA-256)</dt>
                <dd>
                  <code>{record.source_hash}</code>
                </dd>
              </>
            )}
            {record.thread_id && (
              <>
                <dt>Thread ID</dt>
                <dd>
                  <code>{record.thread_id}</code>
                </dd>
              </>
            )}
            {record.email_message_id && (
              <>
                <dt>Message ID</dt>
                <dd>
                  <code>{record.email_message_id}</code>
                </dd>
              </>
            )}
          </dl>
        )}
      </div>

      {historyOpen && (
        <VersionHistoryModal
          record={record}
          onClose={() => setHistoryOpen(false)}
          onRecordUpdated={ctx.onRecordUpdated}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Record edit form
// ---------------------------------------------------------------------------
function RecordEditForm({
  record,
  onCancel,
  onSaved,
}: {
  record: CaseRecord;
  onCancel: () => void;
  onSaved: (r: CaseRecord) => void;
}) {
  const [draft, setDraft] = useState(record.body_text ?? "");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  async function save() {
    if (draft === (record.body_text ?? "")) {
      onCancel();
      return;
    }
    setSaving(true);
    try {
      const { record: next } = await api.updateRecordContent(
        record.id,
        draft,
        reason || undefined,
      );
      onSaved(next);
    } catch (err) {
      window.alert(`Save failed: ${(err as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="record-edit">
      <h4>Edit working copy</h4>
      <textarea
        className="record-edit-textarea"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        rows={Math.min(30, Math.max(6, (draft.match(/\n/g)?.length ?? 0) + 3))}
      />
      <input
        type="text"
        className="record-edit-reason"
        placeholder="Reason for edit (optional — saved to history)"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
      <div className="record-edit-actions">
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={save}
          disabled={saving}
        >
          {saving ? "Saving…" : "Save edit"}
        </Button>
      </div>
      <p className="muted record-edit-note">
        Your previous working copy is preserved in the version history.
        The original source is immutable and always available.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tag editor
// ---------------------------------------------------------------------------
function TagEditor({
  record,
  onRecordUpdated,
}: {
  record: CaseRecord;
  onRecordUpdated: (r: CaseRecord) => void;
}) {
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const current = parseTagsString(record.tags);

  async function commit(next: string[]) {
    setBusy(true);
    try {
      const { record: updated } = await api.updateTags(record.id, next);
      onRecordUpdated(updated);
    } catch (err) {
      window.alert(`Tags: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  const reason = (record.significance_reason ?? "").toLowerCase();
  const suggestions: string[] = [];
  const pool: Array<[string, string]> = [
    ["promise", "Promise"],
    ["deadline", "Key date"],
    ["admission", "Admission"],
    ["denial", "Denial"],
    ["amount", "Amount"],
    ["commitment", "Commitment"],
  ];
  for (const [needle, tag] of pool) {
    if (reason.includes(needle) && !current.includes(tag.toLowerCase())) {
      suggestions.push(tag);
    }
  }

  return (
    <div className="record-tags">
      <div className="record-tags-row">
        {current.map((t) => (
          <span key={t} className="tag-chip in-record">
            {t}
            <button
              type="button"
              className="tag-chip-remove"
              aria-label={`Remove ${t}`}
              onClick={() => commit(current.filter((x) => x !== t))}
              disabled={busy}
            >
              ×
            </button>
          </span>
        ))}
        <input
          className="record-tags-input"
          type="text"
          placeholder={current.length ? "Add tag…" : "Tag this record…"}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              const t = draft.trim().toLowerCase();
              if (t && !current.includes(t)) void commit([...current, t]);
              setDraft("");
            } else if (
              e.key === "Backspace" &&
              draft === "" &&
              current.length > 0
            ) {
              void commit(current.slice(0, -1));
            }
          }}
          disabled={busy}
        />
      </div>
      {suggestions.length > 0 && (
        <div className="record-tags-suggest">
          <span className="muted">Suggested:</span>
          {suggestions.map((s) => (
            <button
              key={s}
              type="button"
              className="tag-chip suggestion"
              onClick={() => commit([...current, s.toLowerCase()])}
              disabled={busy}
            >
              + {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Record body with inline annotation highlights
// ---------------------------------------------------------------------------
function RecordBodyWithAnnotations({
  recordId,
  body,
}: {
  recordId: string;
  body: string;
}) {
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [selected, setSelected] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { annotations } = await api.listAnnotations(recordId);
        if (!cancelled) setAnnotations(annotations);
      } catch {
        /* non-fatal */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [recordId]);

  function pickFromSelection() {
    const sel = window.getSelection();
    const text = sel?.toString().trim() ?? "";
    if (text && body.includes(text)) {
      setSelected(text);
    } else {
      setSelected("");
    }
    setAdding(true);
  }

  async function save() {
    const clean = selected.trim();
    const cleanNote = note.trim();
    if (!clean || !cleanNote) return;
    setSaving(true);
    try {
      const { annotation } = await api.createAnnotation(
        recordId,
        clean,
        cleanNote,
      );
      setAnnotations((prev) => [...prev, annotation]);
      setAdding(false);
      setSelected("");
      setNote("");
    } catch (err) {
      window.alert(`Annotation failed: ${(err as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  async function remove(id: string) {
    if (!window.confirm("Delete this annotation?")) return;
    try {
      await api.deleteAnnotation(id);
      setAnnotations((prev) => prev.filter((a) => a.id !== id));
    } catch (err) {
      window.alert((err as Error).message);
    }
  }

  return (
    <>
      <div className="record-body-heading">
        <h4>Body</h4>
        <Tooltip title="Highlight text and add a note">
          <Button variant="ghost" size="sm" onClick={pickFromSelection}>
            + Annotate
          </Button>
        </Tooltip>
      </div>
      <pre className="body-text">
        {renderHighlighted(body, annotations)}
      </pre>
      {adding && (
        <div className="annotation-form">
          <label>
            Selected text
            <textarea
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
              rows={2}
              placeholder="Paste or retype the passage to highlight"
            />
          </label>
          <label>
            Note
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              autoFocus
              placeholder="Why this matters"
            />
          </label>
          <div className="form-actions">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setAdding(false);
                setSelected("");
                setNote("");
              }}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={save}
              disabled={saving || !selected.trim() || !note.trim()}
            >
              {saving ? "Saving…" : "Save annotation"}
            </Button>
          </div>
        </div>
      )}
      {!loading && annotations.length > 0 && (
        <div className="annotations-list">
          <h4>Annotations</h4>
          {annotations.map((a) => (
            <div key={a.id} className="annotation-item">
              <div className="annotation-quote">"{a.selected_text}"</div>
              <div className="annotation-note">{a.note}</div>
              <Button
                variant="danger"
                size="sm"
                onClick={() => remove(a.id)}
              >
                Delete
              </Button>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function renderHighlighted(
  body: string,
  annotations: Annotation[],
): React.ReactNode[] {
  if (annotations.length === 0) return [body];
  const nodes: React.ReactNode[] = [];
  let cursor = 0;
  type Range = { start: number; end: number; note: string };
  const ranges: Range[] = [];
  for (const a of annotations) {
    const idx = body.indexOf(a.selected_text);
    if (idx < 0) continue;
    ranges.push({
      start: idx,
      end: idx + a.selected_text.length,
      note: a.note,
    });
  }
  ranges.sort((a, b) => a.start - b.start);
  const filtered: Range[] = [];
  for (const r of ranges) {
    const last = filtered[filtered.length - 1];
    if (!last || r.start >= last.end) filtered.push(r);
  }
  for (const r of filtered) {
    if (cursor < r.start) nodes.push(body.slice(cursor, r.start));
    nodes.push(
      <mark
        key={`${r.start}-${r.end}`}
        className="body-mark"
        title={r.note}
      >
        {body.slice(r.start, r.end)}
      </mark>,
    );
    cursor = r.end;
  }
  if (cursor < body.length) nodes.push(body.slice(cursor));
  return nodes;
}

// ---------------------------------------------------------------------------
// Version history modal
// ---------------------------------------------------------------------------
function VersionHistoryModal({
  record,
  onClose,
  onRecordUpdated,
}: {
  record: CaseRecord;
  onClose: () => void;
  onRecordUpdated: (r: CaseRecord) => void;
}) {
  const [versions, setVersions] = useState<RecordVersion[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewing, setViewing] = useState<RecordVersion | null>(null);
  const mountedRef = useRef(true);
  useModalEscape(onClose);

  useEffect(() => {
    mountedRef.current = true;
    (async () => {
      try {
        const { versions } = await api.listVersions(record.id);
        if (mountedRef.current) setVersions(versions);
      } catch (err) {
        if (mountedRef.current) setError((err as Error).message);
      }
    })();
    return () => {
      mountedRef.current = false;
    };
  }, [record.id]);

  async function restore(versionNumber: number) {
    if (
      !window.confirm(
        `Restore version ${versionNumber}? The current working copy will be saved to history first.`,
      )
    )
      return;
    setBusy(true);
    try {
      const { record: updated } = await api.restoreVersion(
        record.id,
        versionNumber,
      );
      onRecordUpdated(updated);
      const { versions: fresh } = await api.listVersions(record.id);
      setVersions(fresh);
    } catch (err) {
      window.alert(`Restore failed: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalBackdrop onClose={onClose}>
      <div
        className="modal-panel version-history-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-head">
          <h3>Version history</h3>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
        {error && <div className="form-error">{error}</div>}
        {!versions && !error && <div className="muted">Loading…</div>}
        {versions && versions.length === 0 && (
          <p className="muted">No history for this record yet.</p>
        )}
        {versions && versions.length > 0 && (
          <ul className="version-list">
            {versions.map((v) => (
              <li key={v.id} className="version-item">
                <div className="version-meta">
                  <div>
                    <strong>Version {v.version_number}</strong>
                    {v.version_number === 1 && (
                      <span className="muted"> — Original, immutable</span>
                    )}
                  </div>
                  <div className="muted">
                    {formatDateTime(v.modified_at)}
                  </div>
                  {v.reason && <div className="muted">{v.reason}</div>}
                </div>
                <div className="version-actions">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setViewing(v)}
                  >
                    View
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => restore(v.version_number)}
                    disabled={busy}
                  >
                    {v.version_number === 1 ? "Restore original" : "Restore"}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
        {viewing && (
          <div className="version-viewer">
            <h4>Version {viewing.version_number}</h4>
            <pre className="body-text">{viewing.content}</pre>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setViewing(null)}
            >
              Close
            </Button>
          </div>
        )}
      </div>
    </ModalBackdrop>
  );
}

// Re-export the attachment icon helper so existing callers in CaseView
// or PDF export can use the same emoji table.
export { attachmentEmoji } from "./AttachmentCard";

// --------------------------------------------------------------------------
// ReceiptDetected — renders a small card in the expanded record view when
// the upload pipeline flagged the record as a receipt. Parses the JSON
// string lazily (receipt_data is stored as TEXT in D1) and gracefully
// silently hides if the blob is missing or unparseable.
// --------------------------------------------------------------------------

const RECEIPT_CURRENCY_SYMBOLS: Record<string, string> = {
  USD: "$",
  GBP: "£",
  EUR: "€",
  CAD: "C$",
  AUD: "A$",
  JPY: "¥",
};

function ReceiptDetected({ record }: { record: CaseRecord }) {
  if (!record.receipt_data) return null;
  let parsed: {
    is_receipt?: boolean;
    vendor?: string | null;
    date?: string | null;
    amount?: number | null;
    currency?: string | null;
    description?: string | null;
  } | null = null;
  try {
    parsed = JSON.parse(record.receipt_data);
  } catch {
    return null;
  }
  if (!parsed || parsed.is_receipt !== true) return null;

  const sym =
    (parsed.currency &&
      RECEIPT_CURRENCY_SYMBOLS[parsed.currency.toUpperCase()]) ??
    (parsed.currency ?? "");
  const amount =
    typeof parsed.amount === "number" && Number.isFinite(parsed.amount)
      ? `${sym}${parsed.amount.toFixed(2)}`
      : null;
  const date = parsed.date ? formatDate(parsed.date, "long") || parsed.date : null;

  return (
    <section className="receipt-detected" aria-label="Receipt detected">
      <header className="receipt-detected-head">
        <span aria-hidden>🧾</span>
        <strong>Receipt detected</strong>
      </header>
      <dl className="receipt-detected-body">
        {parsed.vendor && (
          <>
            <dt>Vendor</dt>
            <dd>{parsed.vendor}</dd>
          </>
        )}
        {date && (
          <>
            <dt>Date</dt>
            <dd>{date}</dd>
          </>
        )}
        {amount && (
          <>
            <dt>Amount</dt>
            <dd className="receipt-detected-amount">{amount}</dd>
          </>
        )}
        {parsed.description && (
          <>
            <dt>Description</dt>
            <dd>{parsed.description}</dd>
          </>
        )}
      </dl>
    </section>
  );
}

// --------------------------------------------------------------------------
// VolumeAwareView — multi-volume wrapper around the existing Compact /
// Comfortable / Reading renderers. Splits orderedEntries into per-volume
// subsets, renders a collapsible header per volume, and runs the chosen
// view function against each subset.
//
// Single-volume cases never reach this path (the parent keeps the old
// single-render). Closed volumes default to collapsed in Compact and
// Comfortable modes, matching the design spec; Reading mode keeps all
// volumes expanded since its pagination already limits what's visible.
// --------------------------------------------------------------------------


interface VolumeAwareViewProps {
  caseId: string;
  volumes: CaseVolume[];
  showSummaryVolumeId?: string | null;
  onShowSummaryHandled?: () => void;
  orderedRecords: { pinned: CaseRecord[]; unpinned: CaseRecord[] };
  viewMode: ViewMode;
  groupByMonth: boolean;
  readingPage: number;
  onReadingPage: (p: number) => void;
  ctx: ViewContext;
  onAskAiAboutVolume?: (volumeId: string) => void;
  // When a scroll-to-record request arrives (from an AI citation or
  // a timeline infographic card click), the containing volume must
  // expand before the parent's scrollIntoView can find the DOM node —
  // closed volumes default to collapsed, which would leave the
  // target record unrendered. Passed through from Timeline so that
  // the scroll and the volume expansion run in lockstep.
  scrollToRecordId?: { id: string; n: number } | null;
}

function findVolumeIdForRecord(
  recordId: string,
  orderedRecords: { pinned: CaseRecord[]; unpinned: CaseRecord[] },
): string | null {
  for (const group of [orderedRecords.pinned, orderedRecords.unpinned]) {
    for (const r of group) {
      if (r.id === recordId) return r.volume_id ?? null;
    }
  }
  return null;
}

function VolumeAwareView({
  caseId,
  volumes,
  showSummaryVolumeId = null,
  onShowSummaryHandled,
  orderedRecords,
  viewMode,
  groupByMonth,
  readingPage,
  onReadingPage,
  ctx,
  onAskAiAboutVolume,
  scrollToRecordId = null,
}: VolumeAwareViewProps) {
  // Ref per closed-volume summary block so "View summary" can scroll
  // it into view. Keyed by volume id because the set of volumes is
  // dynamic (new volumes open, old ones close).
  const summaryRefs = useRef<Map<string, HTMLElement | null>>(new Map());

  // Local expansion state. Both are pure Sets of volume ids so the
  // source of truth lives in Timeline — any "View summary" trigger
  // modifies them and then clears itself. The section header's
  // caret button toggles membership in expandedVolumes only. Summary
  // content inside a closed-volume section is gated by
  // expandedSummaries so it can be hidden/shown independently of the
  // section header.
  //
  // Why local (rather than derived from a prop): the "View summary"
  // prop is a one-shot trigger. After scrolling, the prop clears
  // back to null — if expansion were derived from it, the section
  // would collapse mid-animation. Local state preserves the user's
  // intent after the trigger clears.
  const [expandedVolumes, setExpandedVolumes] = useState<Set<string>>(
    () => new Set(),
  );
  const [expandedSummaries, setExpandedSummaries] = useState<Set<string>>(
    () => new Set(),
  );

  // Seed defaults for each volume the first time we see it. Open
  // volumes default expanded (user expects to see the current
  // records); closed volumes default collapsed + summary visible-
  // when-expanded. Tracked in a ref so re-fetches of the volume
  // list don't clobber user toggles.
  const initializedIds = useRef<Set<string>>(new Set());
  useEffect(() => {
    let addedExpanded = false;
    let addedSummary = false;
    const nextExpanded = new Set(expandedVolumes);
    const nextSummary = new Set(expandedSummaries);
    for (const v of volumes) {
      if (initializedIds.current.has(v.id)) continue;
      initializedIds.current.add(v.id);
      if (v.closed_at === null) {
        nextExpanded.add(v.id);
        addedExpanded = true;
      } else {
        nextSummary.add(v.id);
        addedSummary = true;
      }
    }
    if (addedExpanded) setExpandedVolumes(nextExpanded);
    if (addedSummary) setExpandedSummaries(nextSummary);
    // Intentionally not depending on the two sets — we only want
    // this to fire when the volume list changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [volumes]);

  const toggleVolume = useCallback((volumeId: string) => {
    setExpandedVolumes((prev) => {
      const next = new Set(prev);
      if (next.has(volumeId)) next.delete(volumeId);
      else next.add(volumeId);
      return next;
    });
  }, []);

  // Scroll-to-record trigger: when the parent sets a target record
  // id, pre-emptively expand its containing volume so the record
  // lands in the DOM. The parent's own retry loop then finds the
  // element and scrolls it into view. No scroll happens here — this
  // effect only ensures the target is renderable.
  useEffect(() => {
    if (!scrollToRecordId) return;
    const vid = findVolumeIdForRecord(
      scrollToRecordId.id,
      orderedRecords,
    );
    if (!vid) return;
    setExpandedVolumes((prev) => {
      if (prev.has(vid)) return prev;
      const next = new Set(prev);
      next.add(vid);
      return next;
    });
  }, [scrollToRecordId, orderedRecords]);

  // "View summary" one-shot trigger. Add the volume to both sets
  // (section expanded + summary visible), scroll the summary into
  // view on the next paint, then call onShowSummaryHandled so the
  // parent clears the prop. The section stays open because the
  // local sets persist — the prop going null no longer collapses
  // anything.
  useEffect(() => {
    if (!showSummaryVolumeId) return;
    setExpandedVolumes((prev) => {
      if (prev.has(showSummaryVolumeId)) return prev;
      const next = new Set(prev);
      next.add(showSummaryVolumeId);
      return next;
    });
    setExpandedSummaries((prev) => {
      if (prev.has(showSummaryVolumeId)) return prev;
      const next = new Set(prev);
      next.add(showSummaryVolumeId);
      return next;
    });
    // rAF so the expansion render commits before we measure + scroll.
    const raf = requestAnimationFrame(() => {
      const el = summaryRefs.current.get(showSummaryVolumeId);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      onShowSummaryHandled?.();
    });
    return () => cancelAnimationFrame(raf);
  }, [showSummaryVolumeId, onShowSummaryHandled]);
  // Split each of the pinned + unpinned streams into per-volume
  // buckets, keyed by volume_id. Records whose volume_id doesn't
  // match any known volume land in an "Unassigned" bucket (rare —
  // usually pre-0031 records that never got backfilled).
  const sections = useMemo(() => {
    const pinnedByVol = new Map<string, CaseRecord[]>();
    const unpinnedByVol = new Map<string, CaseRecord[]>();
    const unassignedPinned: CaseRecord[] = [];
    const unassignedUnpinned: CaseRecord[] = [];
    for (const r of orderedRecords.pinned) {
      const vid = r.volume_id ?? null;
      if (!vid) {
        unassignedPinned.push(r);
        continue;
      }
      const arr = pinnedByVol.get(vid) ?? [];
      arr.push(r);
      pinnedByVol.set(vid, arr);
    }
    for (const r of orderedRecords.unpinned) {
      const vid = r.volume_id ?? null;
      if (!vid) {
        unassignedUnpinned.push(r);
        continue;
      }
      const arr = unpinnedByVol.get(vid) ?? [];
      arr.push(r);
      unpinnedByVol.set(vid, arr);
    }
    const ordered = [...volumes].sort(
      (a, b) => a.volume_number - b.volume_number,
    );
    const out: Array<{
      volume: CaseVolume | null;
      records: { pinned: CaseRecord[]; unpinned: CaseRecord[] };
    }> = [];
    for (const v of ordered) {
      out.push({
        volume: v,
        records: {
          pinned: pinnedByVol.get(v.id) ?? [],
          unpinned: unpinnedByVol.get(v.id) ?? [],
        },
      });
    }
    if (unassignedPinned.length > 0 || unassignedUnpinned.length > 0) {
      out.push({
        volume: null,
        records: {
          pinned: unassignedPinned,
          unpinned: unassignedUnpinned,
        },
      });
    }
    return out;
  }, [orderedRecords, volumes]);

  return (
    <div className="timeline-volume-sections">
      {sections.map((section, idx) => {
        const { volume, records: secRecords } = section;
        const isEmpty =
          secRecords.pinned.length === 0 && secRecords.unpinned.length === 0;
        // No-volume fallback bucket.
        if (!volume) {
          if (isEmpty) return null;
          return (
            <section key={`unassigned-${idx}`} className="volume-section">
              <header className="volume-section-header volume-section-unassigned">
                <span>
                  Unassigned records (
                  {secRecords.pinned.length + secRecords.unpinned.length})
                </span>
              </header>
              <VolumeSectionBody
                viewMode={viewMode}
                records={secRecords}
                ctx={ctx}
                groupByMonth={groupByMonth}
                readingPage={readingPage}
                onReadingPage={onReadingPage}
              />
            </section>
          );
        }

        const isOpen = volume.closed_at === null;
        // Reading mode paginates — collapsing the active volume
        // there would hide everything, so force-expand regardless of
        // user toggle.
        const expanded =
          viewMode === "reading"
            ? true
            : expandedVolumes.has(volume.id);
        const summaryVisible = expandedSummaries.has(volume.id);
        const title = volume.title || `Volume ${volume.volume_number}`;
        const dateRange = formatVolumeDateRange(
          volume.first_record_date,
          volume.last_record_date,
        );
        return (
          <section
            key={volume.id}
            className={`volume-section volume-section-${
              isOpen ? "open" : "closed"
            }`}
          >
            <button
              type="button"
              className="volume-section-header"
              onClick={() => toggleVolume(volume.id)}
              aria-expanded={expanded}
            >
              <span className="volume-section-caret" aria-hidden>
                {expanded ? "▼" : "▶"}
              </span>
              <span className="volume-section-number">
                Volume {volume.volume_number}
              </span>
              <span className="volume-section-title">· {title} ·</span>
              <span className="volume-section-meta">
                {dateRange} · {volume.record_count} records
              </span>
              <span
                className={`volume-section-badge volume-section-badge-${
                  isOpen ? "active" : "closed"
                }`}
              >
                {isOpen ? "Active" : "Summary saved"}
              </span>
            </button>
            {expanded && (
              <div className="volume-section-body">
                {isEmpty ? (
                  <div className="muted volume-section-empty">
                    {isOpen
                      ? "No records in the current volume yet."
                      : "No records match the current filters in this volume."}
                  </div>
                ) : (
                  <VolumeSectionBody
                    viewMode={viewMode}
                    records={secRecords}
                    ctx={ctx}
                    groupByMonth={groupByMonth}
                    readingPage={readingPage}
                    onReadingPage={onReadingPage}
                  />
                )}
                {!isOpen && summaryVisible && (
                  <div
                    ref={(el) => {
                      if (el) summaryRefs.current.set(volume.id, el);
                      else summaryRefs.current.delete(volume.id);
                    }}
                  >
                    <VolumeSummary
                      caseId={caseId}
                      volume={volume}
                      onAskAi={onAskAiAboutVolume}
                    />
                  </div>
                )}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}

function VolumeSectionBody({
  viewMode,
  records,
  ctx,
  groupByMonth,
  readingPage,
  onReadingPage,
}: {
  viewMode: ViewMode;
  records: { pinned: CaseRecord[]; unpinned: CaseRecord[] };
  ctx: ViewContext;
  groupByMonth: boolean;
  readingPage: number;
  onReadingPage: (p: number) => void;
}): JSX.Element {
  if (viewMode === "compact") {
    return (
      <CompactView
        orderedRecords={records}
        ctx={ctx}
        groupByMonth={groupByMonth}
      />
    );
  }
  if (viewMode === "comfortable") {
    return (
      <ComfortableView
        orderedRecords={records}
        ctx={ctx}
        groupByMonth={groupByMonth}
      />
    );
  }
  return (
    <ReadingView
      orderedRecords={records}
      ctx={ctx}
      page={readingPage}
      onPage={onReadingPage}
    />
  );
}

function formatVolumeDateRange(
  first: string | null,
  last: string | null,
): string {
  // Month-year labels at the volume-range scope — delegated to the
  // shared utility so the range text matches the month headers
  // inside the Records list.
  const f = first ? formatMonthYear(first) || null : null;
  const l = last ? formatMonthYear(last) || null : null;
  if (f && l) return f === l ? f : `${f} – ${l}`;
  return f ?? l ?? "No date range yet";
}

